import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  Skeleton,
  useTheme,
  Pagination,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Timer as TimerIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  useLazyGetExamAnalyticsQuery,
  useRestartExamMutation,
  useFinishExamMutation,
} from "@/store/api/user/examApi";
import ExamResultDialog from "./ExamResultDialog";
import { ExamStatusBadge } from "../../../components/common";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { clearExamDraft } from "../ExamSession/examLocalStore";

const Statistics = () => {
  const theme = useTheme();
  const [getExamAnalytics, { data: analyticsData, isLoading, error }] =
    useLazyGetExamAnalyticsQuery();
  const [selectedExamAttempt, setSelectedExamAttempt] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [restartExam] = useRestartExamMutation();
  const [finishExam, { isLoading: isFinalizingSubmission }] =
    useFinishExamMutation();
  const [finalizingAttemptId, setFinalizingAttemptId] = useState(null);
  const [page, setPage] = useState(1);
  // isAdmin?
  const isAdmin = useSelector((state) => state.auth.user?.role === "admin");
  const currentUser = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 370,
    });
    getExamAnalytics({ page, limit: 5 });
  }, [page]);

  // Helpers for the in-progress recovery flow. The BE /finish endpoint is
  // idempotent, so it is always safe to call on an in-progress attempt to
  // recover from a previous failed submission (proxy timeout, 401, network
  // drop, browser killed). To surface that affordance correctly we need to
  // know whether the attempt has any answers.
  //
  // BE contract (coordinated):
  //  - Preferred: `getExamAnalytics` response includes `responsesCount` on
  //    each attempt, OR filters phantom in-progress rows out server-side.
  //  - Fallback: attempt object includes a `responses` array.
  //  - If the BE exposes neither, we cannot tell empty vs non-empty apart,
  //    so we ERR ON THE SIDE OF SHOWING the row and the Finalize button —
  //    losing a student's answers is much worse than showing one phantom
  //    row.
  const getResponsesCount = (attempt) => {
    if (typeof attempt?.responsesCount === "number") {
      return attempt.responsesCount;
    }
    if (Array.isArray(attempt?.responses)) {
      return attempt.responses.length;
    }
    return null; // unknown — assume non-empty for safety
  };
  const isKnownEmpty = (attempt) => getResponsesCount(attempt) === 0;
  const canFinalize = (attempt) =>
    !attempt?.completedAt && !isKnownEmpty(attempt);

  const examAttempts = useMemo(() => {
    if (!analyticsData?.data) return [];
    // Hide phantom in-progress rows (Start + leave). We only hide when the
    // BE gives us positive evidence the attempt has zero answers — never
    // when the count is unknown.
    return analyticsData.data.filter((attempt) => {
      if (attempt.completedAt) return true;
      return !isKnownEmpty(attempt);
    });
  }, [analyticsData]);

  const pagination = useMemo(() => {
    if (!analyticsData?.pagination) return {};
    return analyticsData.pagination;
  }, [analyticsData]);

  const overallStats = useMemo(() => {
    if (!examAttempts.length)
      return { totalTests: 0, avgListening: 0, avgReading: 0, avgTotal: 0 };

    const totalTests = examAttempts.length;
    const avgListening =
      examAttempts.reduce(
        (sum, attempt) => sum + (attempt.listeningScore || 0),
        0
      ) / totalTests;
    const avgReading =
      examAttempts.reduce(
        (sum, attempt) => sum + (attempt.readingScore || 0),
        0
      ) / totalTests;

    return { totalTests, avgListening, avgReading, ...analyticsData.analytics };
  }, [examAttempts]);

  const getScoreColor = (score) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleViewDetails = (attempt) => {
    setSelectedExamAttempt(attempt);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedExamAttempt(null);
  };

  const handleRestartExam = async (attempt) => {
    await restartExam(attempt._id);
    clearExamDraft(attempt._id);
    navigate(`/exam-session/${attempt.exam?._id}`);
  };

  const handleFinalizeSubmission = async (attempt) => {
    if (!attempt?._id) return;
    setFinalizingAttemptId(attempt._id);
    try {
      // /finish is idempotent server-side — safe to call on an existing
      // in-progress attempt to recover from a previous failed submission.
      await finishExam(attempt._id).unwrap();
      toast.success("Đã nộp bài thành công.");
      // Refetch analytics so the row flips to completed and shows scores.
      await getExamAnalytics({ page, limit: 5 });
    } catch (error) {
      toast.error(
        error?.data?.message ||
          "Nộp bài thất bại. Vui lòng thử lại sau ít phút."
      );
    } finally {
      setFinalizingAttemptId(null);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 3 }} />
          </Grid>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load statistics. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Account banner — proves which account these analytics belong to.
          Helps surface account confusion (e.g. student logged into wrong
          account wondering why their scores are missing). */}
      {currentUser && (
        <Box
          sx={{
            mb: 3,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            backgroundColor: "rgba(33, 150, 243, 0.06)",
            border: "1px solid rgba(33, 150, 243, 0.2)",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2" sx={{ color: "#1976D2", fontWeight: 600 }}>
            Tài khoản: {currentUser.username}
            {currentUser.name ? ` — ${currentUser.name}` : ""}
          </Typography>
          {currentUser.email && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {currentUser.email}
            </Typography>
          )}
        </Box>
      )}

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <AssessmentIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              {overallStats.totalTests}
            </Typography>
            <Typography variant="body2">Total Tests</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <StarIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              {overallStats.averageScore ? Math.round(overallStats.averageScore) : 0}
            </Typography>
            <Typography variant="body2">Average Score</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              {Math.round(overallStats.avgListening)}
            </Typography>
            <Typography variant="body2">Avg Listening</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              color: "white",
            }}
          >
            <TimerIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">
              {Math.round(overallStats.avgReading)}
            </Typography>
            <Typography variant="body2">Avg Reading</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Test Results */}
      <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, fontSize: { xs: "1.15rem", sm: "1.5rem" } }}>
          Recent Test Results
        </Typography>

        {examAttempts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <AssessmentIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No test results found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Take your first practice test to see your statistics here.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {examAttempts.map((attempt, index) => (
              <Grid item xs={12} key={attempt._id || index}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: theme.shadows[4],
                      transform: "translateY(-2px)",
                      transition: "all 0.3s ease-in-out",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" }, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          sx={{ wordBreak: "break-word", fontSize: { xs: "1rem", sm: "1.25rem" } }}
                        >
                          {attempt.exam?.title || `Test ${index + 1}`}
                        </Typography>
                        <ExamStatusBadge
                          status={
                            attempt.completedAt ? "completed" : "in_progress"
                          }
                          showIcon={true}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Started at: {formatDate(attempt.startedAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completed at: {formatDate(attempt.completedAt)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                        justifyContent: { xs: "flex-end", sm: "flex-start" },
                      }}
                    >
                      {isAdmin && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleRestartExam(attempt)}
                          sx={{
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.error.contrastText,
                            "&:hover": {
                              backgroundColor: theme.palette.error.dark,
                            },
                          }}
                        >
                          Restart Exam
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!attempt.completedAt}
                        onClick={() => handleViewDetails(attempt)}
                      >
                        View Details
                      </Button>
                    </Box>
                    {!attempt.completedAt && canFinalize(attempt) && (
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        sx={{ mr: 1 }}
                        disabled={
                          isFinalizingSubmission &&
                          finalizingAttemptId === attempt._id
                        }
                        onClick={() => handleFinalizeSubmission(attempt)}
                      >
                        {isFinalizingSubmission &&
                        finalizingAttemptId === attempt._id
                          ? "Finalizing..."
                          : "Finalize submission"}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!attempt.completedAt}
                      onClick={() => handleViewDetails(attempt)}
                    >
                      View Details
                    </Button>
                  </Box>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={5}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Listening Score
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="h6"
                            color={getScoreColor(attempt.listeningScore || 0)}
                          >
                            {attempt.listeningScore || 0}
                          </Typography>
                          <Typography variant="body2">/ 495 </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={attempt.listeningScore || 0}
                          sx={{
                            mt: 1,
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: getScoreColor(
                                attempt.listeningScore || 0
                              ),
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Reading Score
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="h6"
                            color={getScoreColor(attempt.readingScore || 0)}
                          >
                            {attempt.readingScore || 0}
                          </Typography>
                          <Typography variant="body2">/ 495</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={attempt.readingScore || 0}
                          sx={{
                            mt: 1,
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: getScoreColor(
                                attempt.readingScore || 0
                              ),
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={1}
                        >
                          Total Score
                        </Typography>
                        <Box
                          sx={{
                            width: { xs: 70, sm: 80 },
                            height: { xs: 70, sm: 80 },
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#1976D2",
                            fontWeight: "bold",
                            fontSize: { xs: "0.85rem", sm: "1rem" },
                            border: "3px solid #1976D2",
                          }}
                        >
                          {attempt.score || 0}/990
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Part Scores */}
                  {attempt.partScores && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Part Scores
                      </Typography>
                      <Grid container spacing={1}>
                        {attempt.partScores.map((partScore, partIndex) => (
                          <Grid item xs={6} sm={4} md={1.7} key={partIndex}>
                            <Paper
                              sx={{
                                p: 1.5,
                                textAlign: "center",
                                bgcolor: "grey.50",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Part {partIndex + 1}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {partScore.correct || 0} /{" "}
                                {partScore.total || 0}
                              </Typography>
                              <Typography
                                variant="caption"
                                color={getScoreColor(
                                  (partScore.correct / partScore.total) * 100 ||
                                    0
                                )}
                              >
                                {Math.round(
                                  (partScore.correct / partScore.total) * 100 ||
                                    0
                                )}
                                %
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}

            {/* Pagination */}
            <Box
              sx={{
                mt: 3,
                mx: "auto",
              }}
            >
              <Pagination
                count={pagination?.totalPages || 0}
                page={page}
                onChange={handlePageChange}
                variant="outlined"
                color="primary"
              />
            </Box>
          </Grid>
        )}
      </Card>

      {/* Exam Result Dialog */}
      <ExamResultDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        examAttempt={selectedExamAttempt}
      />
    </Box>
  );
};

export default Statistics;
